/**
 * Connect / Reconnect dialog (Broker Integration — Phase 1). Broker-agnostic
 * by design: it never hardcodes "if Zerodha do X, if Dhan do Y" — it just
 * renders whatever `GET /broker-accounts/{broker_name}/login-url` says
 * (redirect link, and/or manual-paste text fields), matching the Broker
 * Adapter pattern's promise that a 4th broker needs no frontend changes as
 * long as its adapter's `get_login_info()` fits the same shape.
 */
import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import brokerAccountService from "@/services/brokerAccountService";
import type { BrokerAccountRead, BrokerLoginInfoResponse, BrokerName } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { BROKER_META, credentialFieldLabel } from "./colors";

interface ConnectDialogProps {
  open: boolean;
  broker: BrokerName | null;
  /** Present when reconnecting an existing card; `null`/`undefined` for a
   * first-time connect (see `BrokerAccountsPage`'s `openConnect` vs.
   * `openReconnect`). */
  account?: BrokerAccountRead | null;
  onClose: () => void;
  onConnected: (account: BrokerAccountRead) => void;
}

export function ConnectDialog({ open, broker, account, onClose, onConnected }: ConnectDialogProps) {
  const [loginInfo, setLoginInfo] = useState<BrokerLoginInfoResponse | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // This dialog is a single persistent component instance across opens (see
  // `BrokerAccountsPage`, which always renders one `<ConnectDialog>` and just
  // toggles `open`/`broker`) — it is never unmounted between broker
  // switches. Without this guard, a slow-to-resolve request for a PREVIOUS
  // broker (e.g. Angel One) can resolve AFTER the user has already closed
  // that dialog and reopened it for a different broker (e.g. Dhan),
  // clobbering the new dialog's state with the old broker's stale
  // result/error. `activeBrokerRef` always holds the most current `broker`
  // prop so every async callback below can check it's still relevant before
  // calling setState.
  const activeBrokerRef = useRef(broker);
  useEffect(() => {
    activeBrokerRef.current = broker;
  }, [broker]);

  useEffect(() => {
    if (!open || !broker) return;
    setLoginInfo(null);
    // Reconnecting an existing broker account pre-fills Client ID from the
    // value already saved on that account (see `BrokerAccount.client_id`'s
    // own docstring: this column deliberately survives disconnect/
    // reconnect so it doesn't need to be re-typed) -- still fully
    // editable, and a first-time Connect (no `account` yet) starts blank
    // exactly as before. Every OTHER manual field (access_token/
    // session_token/account_name) still starts blank: those are either
    // session-scoped secrets never returned to the frontend at all, or
    // simply out of scope for this prefill (2026-09-13 Client ID
    // remember-me fix).
    setFields(account?.client_id ? { client_id: account.client_id } : {});
    setSubmitError(null);
    setLoadingInfo(true);
    const requestBroker = broker;
    brokerAccountService
      .getLoginInfo(broker)
      .then((info) => {
        if (activeBrokerRef.current !== requestBroker) return;
        setLoginInfo(info);
      })
      .catch((err) => {
        if (activeBrokerRef.current !== requestBroker) return;
        setInfoError(extractErrorMessage(err));
      })
      .finally(() => {
        if (activeBrokerRef.current !== requestBroker) return;
        setLoadingInfo(false);
      });
  }, [open, broker, account]);

  if (!broker) return null;
  const meta = BROKER_META[broker];
  const isReconnect = Boolean(account);
  const manualFields = loginInfo?.manual_paste_fields ?? [];
  const canSubmitManually = manualFields.length > 0;

  const handleSubmit = async () => {
    setSubmitError(null);
    for (const field of manualFields) {
      if (!fields[field]?.trim()) {
        setSubmitError(`${credentialFieldLabel(field)} is required.`);
        return;
      }
    }
    const requestBroker = broker;
    setSubmitting(true);
    try {
      const result = isReconnect && account
        ? await brokerAccountService.reconnect({ account_id: account.id, credentials: fields })
        : await brokerAccountService.connect({ broker_name: broker, credentials: fields });
      if (activeBrokerRef.current !== requestBroker) return;
      onConnected(result);
    } catch (err) {
      if (activeBrokerRef.current !== requestBroker) return;
      setSubmitError(extractErrorMessage(err));
    } finally {
      if (activeBrokerRef.current === requestBroker) setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isReconnect ? "Reconnect" : "Connect"} {meta.displayName}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {loadingInfo && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          )}
          {infoError && <Alert severity="error">{infoError}</Alert>}
          {submitError && <Alert severity="error">{submitError}</Alert>}

          {loginInfo && !loginInfo.login_url && !loginInfo.requires_manual_paste && (
            <Alert severity="warning">
              {meta.displayName} is not configured yet. Contact your administrator to set up its API
              credentials before connecting.
              {loginInfo.configuration_issues && loginInfo.configuration_issues.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }} data-testid="broker-config-issues">
                  {loginInfo.configuration_issues.map((issue) => (
                    <li key={issue}>
                      <Typography variant="body2">{issue}</Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {loginInfo?.login_url && meta.connectNote && <Alert severity="info">{meta.connectNote}</Alert>}

          {loginInfo?.login_url && (
            <Button
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              component="a"
              href={loginInfo.login_url}
              target={loginInfo.requires_manual_paste ? "_blank" : undefined}
              rel="noopener noreferrer"
              onClick={() => {
                // Zerodha's flow has no manual-paste step — the backend's
                // own OAuth callback completes the connection and redirects
                // back here, so there's nothing left for this dialog to
                // submit once the user leaves.
                if (!loginInfo.requires_manual_paste) onClose();
              }}
            >
              {loginInfo.requires_manual_paste ? `Open ${meta.displayName} Login` : `Continue to ${meta.displayName}`}
            </Button>
          )}

          {loginInfo?.requires_manual_paste && loginInfo.login_url && (
            <Typography variant="caption" color="text.secondary">
              Log in via the link above, then copy the value from the redirected URL and paste it below.
            </Typography>
          )}

          {canSubmitManually &&
            manualFields.map((field) => (
              <TextField
                key={field}
                label={credentialFieldLabel(field)}
                value={fields[field] ?? ""}
                onChange={(e) => setFields((prev) => ({ ...prev, [field]: e.target.value }))}
                fullWidth
                autoComplete="off"
              />
            ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {canSubmitManually && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || loadingInfo}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {isReconnect ? "Reconnect" : "Connect"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ConnectDialog;
